export type GameMode = "deathmatch" | "teams" | "practice";
export type Team = "ember" | "azure" | "none";
export type Gender = "male" | "female";
export type WeaponId = "pistol" | "shotgun" | "machineGun" | "rocket" | "grenade";

/** Keyboard slot 3 (the Ripper) is the default loadout. */
export const DEFAULT_WEAPON_INDEX = 2;

export interface MatchConfig {
  mode: GameMode;
  mapId: string;
  scoreLimit: number;
  seed: number;
}

export interface RosterPlayer {
  id: string;
  name: string;
  gender: Gender;
  team: Team;
  order: number;
}

export interface InputState {
  bits: number;
  aimX: number;
  aimY: number;
  weapon: number;
}

export const InputBits = {
  left: 1 << 0,
  right: 1 << 1,
  jump: 1 << 2,
  dash: 1 << 3,
  shoot: 1 << 4,
  melee: 1 << 5,
  reload: 1 << 6,
} as const;

export interface PlayerState {
  id: string;
  name: string;
  gender: Gender;
  team: Team;
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: -1 | 1;
  health: number;
  weapon: WeaponId;
  ammo: Record<WeaponId, number>;
  reloadFrames: number;
  regenDelayFrames: number;
  regenTickFrames: number;
  grounded: boolean;
  jumpsUsed: number;
  dashFrames: number;
  dashCooldown: number;
  shootCooldown: number;
  meleeCooldown: number;
  invulnerableFrames: number;
  respawnFrames: number;
  kills: number;
  deaths: number;
  score: number;
  inputLatch: number;
}

export interface ProjectileState {
  id: number;
  ownerId: string;
  team: Team;
  weapon: WeaponId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  ttl: number;
  damage: number;
  radius: number;
  bounces: number;
}

export interface DestructibleState {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  health: number;
  maxHealth: number;
}

export interface PickupState {
  id: string;
  kind: "health" | WeaponId;
  x: number;
  y: number;
  active: boolean;
  respawnFrames: number;
}

export interface WorldState {
  frame: number;
  rng: number;
  nextEntityId: number;
  players: Record<string, PlayerState>;
  projectiles: ProjectileState[];
  destructibles: DestructibleState[];
  pickups: PickupState[];
  winnerId: string | null;
}

export interface SimulationEvent {
  kind: "shot" | "hit" | "explosion" | "death" | "pickup" | "dash" | "melee" | "reload";
  frame: number;
  x: number;
  y: number;
  strength?: number;
  playerId?: string;
  attackerId?: string;
  weapon?: WeaponId;
}

export const neutralInput = (): InputState => ({ bits: 0, aimX: 127, aimY: 0, weapon: DEFAULT_WEAPON_INDEX });

export const hasInput = (input: InputState, bit: number): boolean => (input.bits & bit) !== 0;
